import { getApiPrefix } from '../utils/apiHelper';
import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Trash2, Wallet, Loader2, Pencil, Upload, FolderTree, Tags } from "lucide-react";
import DataTable from "../components/ui/data-table";
import { usePermissions } from "../hooks/usePermissions";
import { PageLoader } from "../components/ui/page-loader";
import { EmptyState } from "../components/ui/empty-state";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { ExcelUploadHelper, EXCEL_COLUMNS } from "../components/ui/excel-upload-helper";
import { useTranslation } from "../contexts/I18nContext";

export default function AccountsPage() {
  const { t } = useTranslation();
  const { canEdit } = usePermissions();
  const canEditConti = canEdit("conti");
  
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("conti");
  
  // Account dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ codice: "", nome: "", tipo: "", descrizione: "", categoria_id: "" });
  const [saving, setSaving] = useState(false);
  
  // Category dialog
  const [catDialogOpen, setCatDialogOpen] = useState(false);
  const [editingCatId, setEditingCatId] = useState(null);
  const [catForm, setCatForm] = useState({ codice: "", nome: "", descrizione: "", ordine: 0 });
  
  // Upload
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const prefix = getApiPrefix();
      const [accountsRes, categoriesRes] = await Promise.all([
        api.get(`${prefix}/accounts`),
        api.get(`${prefix}/account-categories`)
      ]);
      setAccounts(accountsRes.data);
      setCategories(categoriesRes.data);
    } catch (err) {
      handleApiError(err, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ========== ACCOUNT HANDLERS ==========
  const handleAccountSubmit = async () => {
    if (!form.codice || !form.nome) {
      handleApiError({ message: t("errors.requiredCodeName") });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        categoria_id: form.categoria_id || null
      };
      if (editingId) {
        await api.put(`${getApiPrefix()}/accounts/${editingId}`, payload);
        showSuccess(t("success.accountUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/accounts`, payload);
        showSuccess(t("success.accountCreated"));
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ codice: "", nome: "", tipo: "", descrizione: "", categoria_id: "" });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorSave"));
    } finally {
      setSaving(false);
    }
  };

  const handleAccountDelete = async (id) => {
    if (!window.confirm(t("confirm.deleteAccount"))) return;
    try {
      await api.delete(`${getApiPrefix()}/accounts/${id}`);
      showSuccess(t("success.accountDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorDeletion"));
    }
  };

  const handleBulkDelete = async (ids) => {
    try {
      await api.post(`${getApiPrefix()}/accounts/bulk-delete`, { ids });
      showSuccess(`${ids.length} conti eliminati`);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorDeletion"));
    }
  };

  const openAccountEdit = (acc) => {
    setEditingId(acc.id);
    setForm({ 
      codice: acc.codice, 
      nome: acc.nome, 
      tipo: acc.tipo || "",
      descrizione: acc.descrizione || "",
      categoria_id: acc.categoria_id || ""
    });
    setDialogOpen(true);
  };

  // ========== CATEGORY HANDLERS ==========
  const handleCategorySubmit = async () => {
    if (!catForm.codice || !catForm.nome) {
      handleApiError({ message: t("errors.requiredCodeName") });
      return;
    }
    setSaving(true);
    try {
      if (editingCatId) {
        await api.put(`${getApiPrefix()}/account-categories/${editingCatId}`, catForm);
        showSuccess(t("success.categoryUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/account-categories`, catForm);
        showSuccess(t("success.categoryCreated"));
      }
      setCatDialogOpen(false);
      setEditingCatId(null);
      setCatForm({ codice: "", nome: "", descrizione: "", ordine: 0 });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorSave"));
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryDelete = async (id) => {
    if (!window.confirm(t("confirm.deleteCategoryWithAccounts"))) return;
    try {
      await api.delete(`${getApiPrefix()}/account-categories/${id}`);
      showSuccess(t("success.categoryDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorDeletion"));
    }
  };

  const openCategoryEdit = (cat) => {
    setEditingCatId(cat.id);
    setCatForm({ 
      codice: cat.codice, 
      nome: cat.nome, 
      descrizione: cat.descrizione || "",
      ordine: cat.ordine || 0
    });
    setCatDialogOpen(true);
  };

  // ========== UPLOAD ==========
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      const res = await api.post(`${getApiPrefix()}/accounts/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      showSuccess(res.data.message);
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.importFailed"));
    } finally {
      setUploading(false);
    }
  };

  // ========== COLUMNS ==========
  const TIPO_LABELS = {
    costo: { label: "Costo", color: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300" },
    ricavo: { label: "Ricavo", color: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300" },
    attivo: { label: "Attivo", color: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300" },
    passivo: { label: "Passivo", color: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" },
  };

  const accountColumns = [
    { key: 'codice', label: t('common.code'), width: '120px' },
    { key: 'nome', label: t('common.name') },
    { 
      key: 'tipo', 
      label: t('common.type'),
      width: '110px',
      render: (value) => {
        if (!value) return <span className="text-muted-foreground text-sm">-</span>;
        const t = TIPO_LABELS[value];
        return t ? (
          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${t.color}`}>
            {t.label}
          </span>
        ) : <span className="text-muted-foreground text-sm">{value}</span>;
      }
    },
    { 
      key: 'categoria_nome', 
      label: t('common.category'),
      render: (value, row) => value ? (
        <Badge variant="secondary" className="font-normal">
          <FolderTree className="h-3 w-3 mr-1" />
          {row.categoria_codice ? `[${row.categoria_codice}] ` : ''}{value}
        </Badge>
      ) : <span className="text-muted-foreground text-sm">-</span>
    },
    { key: 'descrizione', label: t('common.description') },
    { 
      key: 'actions', 
      label: t('common.actions'), 
      align: 'right',
      width: '100px',
      sortable: false, 
      filterable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openAccountEdit(row)} title={t("common.edit")}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleAccountDelete(row.id)} title={t("common.delete")}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      )
    }
  ];

  const categoryColumns = [
    { key: 'codice', label: t('common.code'), width: '100px' },
    { key: 'nome', label: t('common.name') },
    { key: 'ordine', label: 'Ordine', width: '80px' },
    { key: 'descrizione', label: t('common.description') },
    { 
      key: 'conti_count', 
      label: 'Conti',
      width: '80px',
      render: (_, row) => {
        const count = accounts.filter(a => a.categoria_id === row.id).length;
        return <Badge variant="outline">{count}</Badge>;
      }
    },
    { 
      key: 'actions', 
      label: t('common.actions'), 
      align: 'right',
      width: '100px',
      sortable: false, 
      filterable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openCategoryEdit(row)} title={t("common.edit")}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleCategoryDelete(row.id)} title={t("common.delete")}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text="Caricamento piano dei conti..." />;
  }

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex items-center gap-3">
        <Wallet className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("accounts.title")}</h1>
          <p className="text-muted-foreground text-sm">{t('accounts.title')}</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="conti" className="flex items-center gap-2" data-testid="tab-conti">
            <Wallet className="h-4 w-4" />
            {t("accounts.title")} ({accounts.length})
          </TabsTrigger>
          <TabsTrigger value="categorie" className="flex items-center gap-2" data-testid="tab-categorie">
            <Tags className="h-4 w-4" />
            {t("categories.title")} ({categories.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab Conti */}
        <TabsContent value="conti">
          <Card>
            <CardContent className="pt-6">
              <DataTable
                columns={accountColumns}
                data={accounts}
                onDelete={handleBulkDelete}
                idField="id"
                emptyMessage="Nessun conto trovato"
                toolbar={
                  canEditConti && (
                    <div className="flex gap-2">
                      <Button onClick={() => { setEditingId(null); setForm({ codice: "", nome: "", tipo: "", descrizione: "", categoria_id: "" }); setDialogOpen(true); }} data-testid="add-account-btn">
                        <Plus className="h-4 w-4 mr-2" /> {t("accounts.newAccount")}
                      </Button>
                      <ExcelUploadHelper
                        columns={EXCEL_COLUMNS.conti}
                        onUpload={handleUpload}
                        uploading={uploading}
                        buttonText="Importa Excel"
                        title="Struttura File - Piano dei Conti"
                      />
                    </div>
                  )
                }
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Categorie */}
        <TabsContent value="categorie">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tags className="h-5 w-5" />
                Categorie Conti
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Le categorie permettono di raggruppare i conti nel bilancio per competenza
              </p>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={categoryColumns}
                data={categories}
                idField="id"
                emptyMessage="Nessuna categoria. Crea la prima per organizzare i conti."
                toolbar={
                  canEditConti && (
                    <Button onClick={() => { setEditingCatId(null); setCatForm({ codice: "", nome: "", descrizione: "", ordine: 0 }); setCatDialogOpen(true); }} data-testid="add-category-btn">
                      <Plus className="h-4 w-4 mr-2" /> {t("categories.newCategory")}
                    </Button>
                  )
                }
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Conto */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingId(null); setForm({ codice: "", nome: "", tipo: "", descrizione: "", categoria_id: "" }); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? t("accounts.editAccount") : t("accounts.newAccount")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('common.code')} *</Label>
                <Input 
                  value={form.codice} 
                  onChange={(e) => setForm({ ...form, codice: e.target.value })} 
                  placeholder="Es: 7.01.001" 
                  data-testid="input-codice"
                />
              </div>
              <div>
                <Label>{t('common.name')} *</Label>
                <Input 
                  value={form.nome} 
                  onChange={(e) => setForm({ ...form, nome: e.target.value })} 
                  placeholder={t("common.name")} 
                  data-testid="input-nome"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("common.type")}</Label>
                <Select value={form.tipo || "__none__"} onValueChange={(v) => setForm({ ...form, tipo: v === "__none__" ? "" : v })}>
                  <SelectTrigger data-testid="select-tipo">
                    <SelectValue placeholder="Seleziona tipo..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessun tipo</SelectItem>
                    <SelectItem value="costo">{t('common.cost')}</SelectItem>
                    <SelectItem value="ricavo">{t('common.revenue')}</SelectItem>
                    <SelectItem value="attivo">{t('common.active')}</SelectItem>
                    <SelectItem value="passivo">Passivo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t("common.category")}</Label>
                <SearchableSelect
                  options={[
                    { value: "__none__", label: "Nessuna categoria" },
                    ...categories.map(c => ({ value: c.id, label: `[${c.codice}] ${c.nome}` }))
                  ]}
                  value={form.categoria_id}
                  onValueChange={(v) => setForm({ ...form, categoria_id: v === "__none__" ? "" : v })}
                  placeholder={t("common.selectPlaceholder")}
                  searchPlaceholder={t("common.searchPlaceholder")}
                  testId="select-categoria"
                />
              </div>
            </div>
            <div>
              <Label>{t("common.description")}</Label>
              <Textarea 
                value={form.descrizione} 
                onChange={(e) => setForm({ ...form, descrizione: e.target.value })} 
                placeholder={t("common.optional")} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleAccountSubmit} disabled={saving} data-testid="save-account-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingId ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Categoria */}
      <Dialog open={catDialogOpen} onOpenChange={(open) => { setCatDialogOpen(open); if (!open) { setEditingCatId(null); setCatForm({ codice: "", nome: "", descrizione: "", ordine: 0 }); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCatId ? t("categories.editCategory") : t("categories.newCategory")}</DialogTitle>
            <DialogDescription>
              Le categorie raggruppano i conti nel bilancio per competenza
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('common.code')} *</Label>
                <Input 
                  value={catForm.codice} 
                  onChange={(e) => setCatForm({ ...catForm, codice: e.target.value.toUpperCase() })} 
                  placeholder="Es: PERS" 
                />
              </div>
              <div>
                <Label>Ordine</Label>
                <Input 
                  type="number"
                  value={catForm.ordine} 
                  onChange={(e) => setCatForm({ ...catForm, ordine: parseInt(e.target.value) || 0 })} 
                  placeholder="0" 
                />
              </div>
            </div>
            <div>
              <Label>{t('common.name')} *</Label>
              <Input 
                value={catForm.nome} 
                onChange={(e) => setCatForm({ ...catForm, nome: e.target.value })} 
                placeholder="Es: Costi del Personale" 
              />
            </div>
            <div>
              <Label>{t("common.description")}</Label>
              <Textarea 
                value={catForm.descrizione} 
                onChange={(e) => setCatForm({ ...catForm, descrizione: e.target.value })} 
                placeholder={t("common.optional")} 
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleCategorySubmit} disabled={saving} data-testid="save-category-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingCatId ? "Salva" : "Crea"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
